import { Analytics } from '@analytics';
import { generateBrandImage, uploadBrandImage } from '@api/company';
import { CircularProgress } from '@material-ui/core';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import { EXPERIMENTAL_GENERATE_BRAND_FROM_SITE } from '@utils/experimentalFeatures';
import CustomSection from '../CustomSection';
import { useSetupPreview } from '../SetupPreviewContext';
import GenerateFromSiteBar from './GenerateFromSiteBar';
import messages from './messages';

const HEX_RE = /^$|^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const brandingSchema = yup.object({
  companyName: yup.string(),
  headline: yup.string(),
  subtext: yup.string(),
  primaryColorHex: yup.string().matches(HEX_RE, 'Enter a hex colour like #1a2b3c'),
  secondaryColorHex: yup.string().matches(HEX_RE, 'Enter a hex colour like #1a2b3c'),
  backgroundColorHex: yup.string().matches(HEX_RE, 'Enter a hex colour like #1a2b3c'),
  textColorHex: yup.string().matches(HEX_RE, 'Enter a hex colour like #1a2b3c'),
  headingColorHex: yup.string().matches(HEX_RE, 'Enter a hex colour like #1a2b3c'),
  whiteLogoBackground: yup.boolean()
});

const inputClass =
  'w-full rounded-xl border border-solid border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-colors';
const labelClass = 'text-sm font-semibold text-slate-700 mb-1.5 block';

function TextInput({ label, name, value, onChange, error, placeholder, multiline }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div>
      <label className={labelClass} htmlFor={name}>
        {label}
      </label>
      <Tag
        id={name}
        name={name}
        className={`${inputClass} ${multiline ? 'min-h-[76px] resize-y' : ''}`}
        value={value || ''}
        placeholder={placeholder}
        onChange={e => onChange(name, e.target.value)}
      />
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
    </div>
  );
}

// A colour swatch that opens the native picker, plus an editable hex field.
function ColorField({ label, name, value, onChange, error }) {
  const swatch = HEX_RE.test(value || '') && value ? value : '#ffffff';
  return (
    <div>
      <label className={labelClass} htmlFor={name}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <label
          className="relative h-9 w-9 shrink-0 rounded-lg border border-solid border-slate-300 cursor-pointer overflow-hidden"
          style={{ backgroundColor: swatch }}>
          <input
            type="color"
            aria-label={label}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            value={swatch}
            onChange={e => onChange(name, e.target.value)}
          />
        </label>
        <input
          id={name}
          name={name}
          className={inputClass}
          value={value || ''}
          placeholder="#000000"
          onChange={e => onChange(name, e.target.value)}
        />
      </div>
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
    </div>
  );
}

// Immediate upload on select (reuses the brand-image endpoint); the returned URL
// is written into the Formik field and persisted with the rest on Save. When
// `generatable`, a "Generate with AI" button (card 94) produces the image from the
// company's tours server-side and feeds the returned URL through the same path.
function ImageField({ label, hint, field, companyId, value, onUploaded, generatable, brand }) {
  const intl = useIntl();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const inputId = `brand-image-${field}`;
  const busy = uploading || generating;

  // The logo renders on the operator's brand background in TA-Tipping, so preview
  // the upload box on that same colour rather than white — a white/light logo is
  // invisible on a white box once a coloured brand background is set. Header and
  // footer images keep the neutral white preview.
  const logoPreviewBg =
    field === 'logoImage' && HEX_RE.test(brand?.backgroundColorHex || '')
      ? brand.backgroundColorHex
      : undefined;

  const handleChange = async e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const url = await uploadBrandImage(companyId, field, file);
      onUploaded(field, url);
    } catch (err) {
      console.error('Brand image upload failed', err);
      setError(intl.formatMessage(messages.uploadError));
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (busy) return;
    setError('');
    setGenerating(true);
    Analytics.track('Brand image generated', { page: 'setup', field });
    try {
      const url = await generateBrandImage(companyId, field, brand);
      onUploaded(field, url);
    } catch (err) {
      console.error('Brand image generation failed', err);
      setError(intl.formatMessage(messages.generateError));
    } finally {
      setGenerating(false);
    }
  };

  // Clear the image and return to the default (no image). Empty string flows
  // through Formik + live preview and persists on Save; TA-Tipping renders
  // nothing when the field is empty, so the operator is back to the default look.
  const handleClear = () => {
    setError('');
    onUploaded(field, '');
    Analytics.track('Brand image removed', { page: 'setup', field });
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      {hint && <p className="text-xs text-slate-500 -mt-1 mb-1.5">{hint}</p>}
      <div className="relative">
        <label
          htmlFor={inputId}
          style={logoPreviewBg ? { backgroundColor: logoPreviewBg } : undefined}
          className={`flex flex-col items-center justify-center gap-2 h-28 rounded-xl border border-dashed border-slate-300 bg-white transition-colors overflow-hidden ${
            busy
              ? 'pointer-events-none opacity-90'
              : 'cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40'
          }`}>
          {busy ? (
            <>
              <CircularProgress size={22} />
              <span className="text-xs text-slate-500">
                {generating
                  ? intl.formatMessage(messages.generating)
                  : intl.formatMessage(messages.uploading)}
              </span>
            </>
          ) : value ? (
            <img src={value} alt={label} className="h-full w-full object-contain p-1.5" />
          ) : (
            <>
              <span className="text-2xl leading-none text-slate-400">+</span>
              <span className="text-xs text-slate-500">Upload</span>
            </>
          )}
        </label>
        {value && !busy && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={intl.formatMessage(messages.removeImage)}
            title={intl.formatMessage(messages.removeImage)}
            className="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-solid border-slate-200 bg-white/90 text-slate-500 shadow-sm leading-none hover:bg-white hover:text-red-500 transition-colors">
            <span aria-hidden="true" className="text-base leading-none">
              ×
            </span>
          </button>
        )}
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={handleChange}
      />
      {generatable && (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={busy}
          className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-solid border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60">
          <span aria-hidden="true">✨</span>
          {generating
            ? intl.formatMessage(messages.generating)
            : intl.formatMessage(messages.generateWithAi)}
        </button>
      )}
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
    </div>
  );
}

// A labelled on/off switch styled to match the form (indigo accent). Writes a
// boolean into the Formik field via the shared `set` (setFieldValue) handler.
function ToggleField({ label, description, name, checked, onChange }) {
  return (
    <label htmlFor={name} className="flex items-center justify-between gap-4 cursor-pointer">
      <span>
        <span className="text-sm font-semibold text-slate-700 block">{label}</span>
        {description && <span className="block text-xs text-slate-500 mt-0.5">{description}</span>}
      </span>
      <span className="relative inline-flex shrink-0">
        <input
          id={name}
          name={name}
          type="checkbox"
          checked={!!checked}
          onChange={e => onChange(name, e.target.checked)}
          className="peer sr-only"
        />
        <span className="h-6 w-11 rounded-full bg-slate-300 transition-colors peer-checked:bg-indigo-500" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

const defaultBranding = {
  companyName: '',
  headline: '',
  subtext: '',
  primaryColorHex: '',
  secondaryColorHex: '',
  backgroundColorHex: '',
  textColorHex: '',
  headingColorHex: '',
  whiteLogoBackground: false
};

const BrandingSection = ({ companyId, branding, onSave }) => {
  const intl = useIntl();
  const { updatePreview } = useSetupPreview();

  const handleSave = values => {
    Analytics.track('Branding saved', { page: 'setup' });
    onSave(values);
  };

  const renderBody = ({ values, errors, setFieldValue }) => {
    const set = (name, val) => setFieldValue(name, val);
    const colours = [
      { name: 'primaryColorHex', label: intl.formatMessage(messages.primary) },
      { name: 'secondaryColorHex', label: intl.formatMessage(messages.secondary) },
      { name: 'backgroundColorHex', label: intl.formatMessage(messages.background) },
      { name: 'textColorHex', label: intl.formatMessage(messages.text) },
      { name: 'headingColorHex', label: intl.formatMessage(messages.heading) }
    ];
    const imageFields = [
      {
        field: 'headerImage',
        label: intl.formatMessage(messages.headerImage),
        hint: intl.formatMessage(messages.headerImageHint),
        generatable: true
      },
      {
        field: 'logoImage',
        label: intl.formatMessage(messages.logoImage),
        hint: intl.formatMessage(messages.logoImageHint),
        generatable: false
      },
      {
        field: 'footerImage',
        label: intl.formatMessage(messages.footerImage),
        hint: intl.formatMessage(messages.footerImageHint),
        generatable: true
      }
    ];

    // Card 102 (experimental) — merge the scanned brand draft into the form.
    // Only fields the scan actually produced overwrite; everything lands in
    // Formik (and so the live preview) but persists only on Save.
    const applyGeneratedBranding = generated => {
      const fields = [
        'companyName',
        'headline',
        'subtext',
        'primaryColorHex',
        'secondaryColorHex',
        'backgroundColorHex',
        'textColorHex',
        'headingColorHex',
        'logoImage',
        'fontUrl'
      ];
      fields.forEach(name => {
        if (generated[name]) setFieldValue(name, generated[name]);
      });
    };

    return (
      <div className="flex flex-col gap-5 w-full">
        {EXPERIMENTAL_GENERATE_BRAND_FROM_SITE && (
          <GenerateFromSiteBar companyId={companyId} onGenerated={applyGeneratedBranding} />
        )}
        <TextInput
          label={intl.formatMessage(messages.companyName)}
          name="companyName"
          value={values.companyName}
          onChange={set}
          error={errors.companyName}
        />
        <TextInput
          label={intl.formatMessage(messages.headline)}
          name="headline"
          value={values.headline}
          onChange={set}
          error={errors.headline}
        />
        <TextInput
          label={intl.formatMessage(messages.subtext)}
          name="subtext"
          value={values.subtext}
          onChange={set}
          error={errors.subtext}
          multiline
        />

        <div>
          <div className="text-sm font-bold text-slate-800 mb-3">
            {intl.formatMessage(messages.colours)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {colours.map(c => (
              <ColorField
                key={c.name}
                label={c.label}
                name={c.name}
                value={values[c.name]}
                onChange={set}
                error={errors[c.name]}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-bold text-slate-800 mb-3">
            {intl.formatMessage(messages.images)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {imageFields.map(img => (
              <ImageField
                key={img.field}
                label={img.label}
                hint={img.hint}
                field={img.field}
                companyId={companyId}
                value={values[img.field]}
                onUploaded={setFieldValue}
                generatable={img.generatable}
                brand={{
                  primaryColorHex: values.primaryColorHex,
                  secondaryColorHex: values.secondaryColorHex,
                  backgroundColorHex: values.backgroundColorHex
                }}
              />
            ))}
          </div>
          <div className="mt-4">
            <ToggleField
              label={intl.formatMessage(messages.whiteLogoBackground)}
              description={intl.formatMessage(messages.whiteLogoBackgroundHelp)}
              name="whiteLogoBackground"
              checked={values.whiteLogoBackground}
              onChange={set}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <CustomSection
      id="Branding"
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      renderBody={renderBody}
      buttonTitle={intl.formatMessage(messages.save)}
      onSave={handleSave}
      onValuesChange={values => updatePreview({ branding: values })}
      initialValues={{ ...defaultBranding, ...(branding || {}) }}
      schema={brandingSchema}
    />
  );
};

export default BrandingSection;
